import React, { useEffect, useMemo, useState } from "react";
import moment, { Moment } from "moment";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux/store";
import { LoadingSpinner } from "../../../../components/ui/loadinSpinner";
import DataRangeSelector from "../../../../components/dataRangeSelector";
import StackedBarChart from "../../../../components/charts/stackedBarChart";
import { getHospitalsPatientTypeOccupiedGraphData } from "../../../../redux/mis/misReducer";
import { RootState } from "../../../../redux/store";

interface OccupiedBedItem {
    patientType: string;
    location: string;
    month: string;
    year: number;
    occupiedBeds: number;
}

function InPatientOccupied({
    country,
    state,
    district,
    taluk,
    hobli,
    serviceType,
    filterApplyToggle,
    title
}: {
    country: string;
    state: string;
    district: string;
    taluk: string;
    hobli: string;
    serviceType: number;
    filterApplyToggle: boolean;
    title?: string;
}) {
    const dispatch = useAppDispatch();

    const currentDate = new Date();
    const [fromDate, setFromDate] = useState<Moment>(
        moment(currentDate).subtract(11, "months")
    );
    const [toDate, setToDate] = useState<Moment>(moment(currentDate));

    const { data, isLoading } = useSelector(
        (state: RootState) => state.occupiedBedGraph
    );

    /* ---------------------------------------------
       FETCH DATA
    ---------------------------------------------- */
    useEffect(() => {
        dispatch(
            getHospitalsPatientTypeOccupiedGraphData({
                country,
                state,
                district,
                taluk,
                hobli,
                serviceType,
                fromDate: moment(fromDate).format("YYYY-MM-DD"),
                toDate: moment(toDate).format("YYYY-MM-DD"),
            })
        );
    }, [fromDate, toDate, filterApplyToggle]);

    /* ---------------------------------------------
       TRANSFORM → STACKED BAR (DYNAMIC LOCATIONS)
    ---------------------------------------------- */
    // const chartData = useMemo(() => {
    //     if (!data.length) {
    //         return { labels: [], datasets: [] };
    //     }

    //     // Month-Year labels
    //     const labels = Array.from(
    //         new Set(data.map(d => `${d.month} ${d.year}`))
    //     );

    //     // Unique locations (ICU, Ward, etc.)
    //     const locations = Array.from(
    //         new Set(data.map(d => d.location))
    //     );

    //     const datasets = locations.map((location, index) => {
    //         const values = labels.map(label => {
    //             const [month, year] = label.split(" ");

    //             const record = data.find(
    //                 d =>
    //                     d.location === location &&
    //                     d.month === month &&
    //                     d.year === Number(year)
    //             );

    //             return record?.occupiedBeds ?? 0;
    //         });

    //         const total = values.reduce((a, b) => a + b, 0);

    //         return {
    //             label: `${location} (${total})`,
    //             data: values,
    //             backgroundColor: [
    //                 "#4FD1C5",
    //                 "#F6AD55",
    //                 "#90CDF4",
    //                 "#FC8181",
    //                 "#D6BCFA",
    //             ][index % 5], // auto colors
    //             barThickness: 28,             // ✅ fixed width
    //             maxBarThickness: 28,          // ✅ prevent auto stretch
    //             categoryPercentage: 0.8,      // ✅ spacing inside category
    //             barPercentage: 0.9,
    //         };
    //     });

    //     return { labels, datasets };
    // }, [data]);
    const chartData = useMemo(() => {
        if (!data.length) return { labels: [], datasets: [] };

        // Generate all month-year labels between fromDate and toDate
        const start = moment(fromDate);
        const end = moment(toDate);
        const labels: string[] = [];

        const current = start.clone();
        while (current.isSameOrBefore(end, "month")) {
            labels.push(current.format("MMM YYYY")); // e.g., "Jan 2025"
            current.add(1, "month");
        }

        // Unique locations (ICU, Ward, etc.)
        const locations = Array.from(new Set(data.map(d => d.location)));

        const datasets = locations.map((location, index) => {
            const values = labels.map(label => {
                const [month, year] = label.split(" ");

                const record = data.find(
                    d =>
                        d.location === location &&
                        d.month === month &&
                        d.year === Number(year)
                );

                return record?.occupiedBeds ?? 0; // fill missing months with 0
            });

            const total = values.reduce((a, b) => a + b, 0);

            return {
                label: `${location} (${total})`,
                data: values,
                backgroundColor: [
                    "#4FD1C5",
                    "#F6AD55",
                    "#90CDF4",
                    "#FC8181",
                    "#D6BCFA",
                ][index % 5],
                borderWidth: 1,

                // barThickness: 28,
                // maxBarThickness: 28,
                // categoryPercentage: 0.8,
                // barPercentage: 0.9,
            };
        });

        return { labels, datasets };
    }, [data, fromDate, toDate]);

    /* ---------------------------------------------
       RENDER
    ---------------------------------------------- */


    // function useCounter() {
    //     const [count, setCount] = React.useState(0);

    //     const increment = () => setCount(c => c + 1);

    //     return { count, increment };
    // }
    // function Counter() {
    //     const { count, increment } = useCounter();

    //     return <button onClick={increment}>{count}</button>;
    // }


    return (
        <div className="p-4 bg-white shadow-sm border rounded-lg w-full pb-20">
            <div className="flex justify-between pb-4">
                <span className="text-18 font-medium text-primary">
                    {title}
                </span>
                {/* {Counter()} */}
                <DataRangeSelector
                    fromDate={fromDate}
                    setFromDate={setFromDate as any}
                    toDate={toDate}
                    setToDate={setToDate as any}
                    py="py-[8px]"
                    border="border-gray-300"
                    borderRadius="rounded-lg"
                    shadow
                    position="right-0"
                />
            </div>

            {isLoading ? (
                <div className="flex justify-center items-center h-full">
                    <LoadingSpinner />
                </div>
            ) : chartData.labels.length ? (
                <StackedBarChart
                    data={chartData}
                    year={Array.from(new Set(data.map(d => d.year)) as any)}
                />
            ) : (
                <div className="flex justify-center items-center h-full">
                    <span className="text-18 font-medium text-primary">
                        No Data Found
                    </span>
                </div>
            )}
        </div>
    );
}

export default InPatientOccupied;
